import classNames from 'classnames';
import { Button } from 'components/atoms/Button';
import { useAIChatContext } from 'components/organisms/AIChat/AIChat.context';
import { sharedMarkdownComponents } from 'components/organisms/AIChat/utils'; // TODO: move to utils
import { AnimatePresence, motion } from 'motion/react';
import { useCallback, useRef, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { ConversationMessageThinkingPart } from 'types/ai.types';
import { ChevronRightIcon } from 'utils/icons';

type AIMessagePartThinkingProps = {
  messageId: string;
  messagePart: ConversationMessageThinkingPart;
  isSmall?: boolean;
  isLastPart?: boolean;
};

const AIMessagePartThinking: FC<AIMessagePartThinkingProps> = ({
  messageId,
  messagePart,
  isSmall,
  isLastPart,
}) => {
  const { t } = useTranslation();
  const { isStreaming } = useAIChatContext();

  // Use ref to persist expanded state across re-renders during streaming
  const isExpandedRef = useRef(false);
  const [, forceUpdate] = useState(0);

  const handleToggle = useCallback(() => {
    isExpandedRef.current = !isExpandedRef.current;
    forceUpdate((prev) => prev + 1); // Force re-render
  }, []);

  if (!messagePart.content) return null;

  const shouldAnimate = isStreaming && isLastPart;

  return (
    <div className="relative transition-colors rounded-lg !w-[80%]">
      <Button
        className={classNames(
          'select-none transition-colors cursor-pointer flex items-center gap-1 w-full',
          {
            'text-xs': isSmall,
            'text-sm': !isSmall,
            'text-shimmer-animation': shouldAnimate,
            'text-primary-600': !shouldAnimate,
          },
        )}
        onClick={handleToggle}
        structure="text"
        asDefault
      >
        {t('ai_bot.thinking.title')}

        <ChevronRightIcon
          className={classNames(
            'h-5 w-5 text-primary-400 transition-transform duration-300',
            { 'rotate-90': isExpandedRef.current },
          )}
          aria-hidden="true"
        />
      </Button>

      <AnimatePresence>
        {isExpandedRef.current && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{
              height: 'auto',
              opacity: 1,
              transition: {
                height: { duration: 0.3 },
                opacity: { duration: 0.2, delay: 0.1 },
              },
            }}
            exit={{
              height: 0,
              opacity: 0,
              transition: {
                height: { duration: 0.3 },
                opacity: { duration: 0.1 },
              },
            }}
            style={{ overflow: 'hidden' }}
          >
            <div
              className={classNames(
                'mt-3 pl-3 border-l-2 border-gray-300 space-y-2 text-gray-500 opacity-75',
                { 'text-xs': isSmall, 'text-sm': !isSmall },
              )}
            >
              <ReactMarkdown
                key={messageId}
                remarkPlugins={[remarkGfm]}
                components={sharedMarkdownComponents}
              >
                {messagePart.content}
              </ReactMarkdown>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AIMessagePartThinking;
